import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FaBook,
  FaRocket,
  FaSearch,
  FaClock,
  FaArrowRight,
  FaBrain,
  FaCode,
  FaGraduationCap,
  FaLightbulb,
} from 'react-icons/fa';

interface Doc {
  id: string;
  title: string;
  slug: string;
  category: string;
  excerpt?: string;
  status: 'draft' | 'published';
  order?: number;
  metadata?: {
    readTime?: number;
  };
}

const DocsHome: React.FC = () => {
  const [docs, setDocs] = useState<Doc[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const API_URL = import.meta.env.VITE_API_BACKEND_URL;
  const MAIN_SITE = import.meta.env.VITE_MAIN_SITE_URL || 'https://nostrahealth.com';

  useEffect(() => {
    fetchDocs();
  }, []);

  const fetchDocs = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/documentation/dev-docs`);
      const data = await res.json();
      // API returns array directly, not wrapped in {success, data}
      const docsArray = Array.isArray(data) ? data : (data.data || []);
      // Only show published docs, sorted by order
      const published = docsArray
        .filter((doc: Doc) => doc.status === 'published')
        .sort((a: Doc, b: Doc) => (a.order || 0) - (b.order || 0));
      setDocs(published);
    } catch (err) {
      console.error('Error fetching docs:', err);
    } finally {
      setLoading(false);
    }
  };

  const categories = Array.from(new Set(docs.map((doc) => doc.category)));

  const filteredDocs = docs.filter((doc) =>
    searchQuery
      ? doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (doc.excerpt && doc.excerpt.toLowerCase().includes(searchQuery.toLowerCase()))
      : true
  );

  const docsByCategory = categories.reduce((acc, category) => {
    acc[category] = filteredDocs.filter((doc) => doc.category === category);
    return acc;
  }, {} as Record<string, Doc[]>);

  const quickStartDocs = filteredDocs.filter((doc) =>
    doc.category?.toLowerCase().includes('getting started')
  ).slice(0, 3);

  return (
    <>
      <Helmet>
        <title>NostraHealth Documentation</title>
        <meta
          name="description"
          content="Complete documentation for NostraHealth API, SDKs, guides, and integration resources"
        />
        <meta property="og:title" content="NostraHealth Documentation" />
        <meta property="og:description" content="API docs, guides, and technical resources" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://docs.nostrahealth.com" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-lightPurple via-backgroundColor to-white">
        {/* Header */}
        <header className="backdrop-blur-xl bg-white/70 border-b border-white/20 sticky top-0 z-50 shadow-lg">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <a
                href="/"
                className="text-2xl font-bold bg-gradient-to-r from-Color to-brightColor bg-clip-text text-transparent hover:from-hoverColor hover:to-Color transition-all"
              >
                NostraHealth <span className="text-sm font-normal text-gray-600">/ Docs</span>
              </a>
              <nav className="flex items-center gap-6">
                <a href={MAIN_SITE} className="text-gray-700 hover:text-Color font-medium transition-colors">
                  Home
                </a>
                <a
                  href={import.meta.env.VITE_BLOG_URL}
                  className="text-gray-700 hover:text-Color font-medium transition-colors"
                >
                  Blog
                </a>
                <a
                  href={`${MAIN_SITE}/pricing`}
                  className="px-6 py-2 bg-gradient-to-r from-Color to-brightColor text-white rounded-full hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold"
                >
                  Get Started
                </a>
              </nav>
            </div>
          </div>
        </header>

        {/* Hero */}
        <div className="relative bg-gradient-to-r from-Color/20 via-brightColor/20 to-accentPurple/20 py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-Color/10 to-transparent"></div>
          <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
            <h1 className="text-6xl font-black mb-6 bg-gradient-to-r from-Color via-brightColor to-accentPurple bg-clip-text text-transparent">
              NostraHealth Documentation
            </h1>
            <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto font-medium">
              Everything you need to integrate and build with NostraHealth's AI-powered healthcare platform
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative group">
                <FaSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-Color text-lg z-10" />
                <input
                  type="text"
                  placeholder="Search documentation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-14 pr-6 py-4 rounded-2xl backdrop-blur-xl bg-white/80 border-2 border-white/40 text-gray-900 text-lg focus:outline-none focus:border-Color/50 focus:shadow-2xl transition-all placeholder-gray-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Start Cards */}
        {quickStartDocs.length > 0 && !searchQuery && (
          <div className="max-w-7xl mx-auto px-6 -mt-8 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {quickStartDocs.map((doc) => (
                <Link
                  key={doc.id}
                  to={`/${doc.slug}`}
                  className="backdrop-blur-xl bg-white/70 rounded-2xl p-6 border border-white/30 shadow-glass hover:shadow-glass-hover hover:-translate-y-2 hover:border-Color/50 transition-all duration-300 group"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-4 bg-gradient-to-br from-Color/20 to-brightColor/20 rounded-xl group-hover:scale-110 transition-transform duration-300">
                      <FaRocket className="text-Color text-2xl" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-Color transition-colors">{doc.title}</h3>
                      <p className="text-sm text-gray-600 line-clamp-2">{doc.excerpt || 'Get started with this guide'}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Documentation Grid */}
        <div className="max-w-7xl mx-auto px-6 py-12">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-Color"></div>
            </div>
          ) : filteredDocs.length === 0 ? (
            <div className="text-center py-20">
              <div className="backdrop-blur-xl bg-white/60 rounded-3xl p-12 border border-white/40 shadow-glass inline-block">
                <FaBook className="mx-auto text-gray-300 text-6xl mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">No documentation found</h3>
                <p className="text-gray-600">Try adjusting your search</p>
              </div>
            </div>
          ) : (
            <div className="space-y-12">
              {categories.map((category) => {
                const categoryDocs = docsByCategory[category];
                if (!categoryDocs || categoryDocs.length === 0) return null;

                const categoryIcon = category.toLowerCase().includes('getting started') ? <FaRocket /> :
                                    category.toLowerCase().includes('ai') ? <FaBrain /> :
                                    category.toLowerCase().includes('api') ? <FaCode /> :
                                    category.toLowerCase().includes('tutorial') ? <FaGraduationCap /> :
                                    category.toLowerCase().includes('guide') ? <FaLightbulb /> :
                                    <FaBook />;

                return (
                  <div key={category} className="backdrop-blur-xl bg-white/60 rounded-3xl p-8 border border-white/40 shadow-glass">
                    <h2 className="text-3xl font-black mb-6 flex items-center gap-3 bg-gradient-to-r from-Color to-brightColor bg-clip-text text-transparent">
                      {categoryIcon}
                      {category}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {categoryDocs.map((doc) => (
                        <Link
                          key={doc.id}
                          to={`/${doc.slug}`}
                          className="group backdrop-blur-xl bg-white/70 rounded-2xl p-6 border border-white/30 hover:border-Color/50 shadow-glass hover:shadow-glass-hover hover:scale-[1.03] hover:-translate-y-1 transition-all duration-300"
                        >
                          <div className="flex items-start gap-4 mb-4">
                            <div className="p-3 bg-gradient-to-br from-Color/20 to-brightColor/20 rounded-xl group-hover:scale-110 transition-transform">
                              {categoryIcon}
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-Color transition-colors">
                                {doc.title}
                              </h3>
                            </div>
                          </div>
                          {doc.excerpt && (
                            <p className="text-sm text-gray-600 mb-4 line-clamp-2">{doc.excerpt}</p>
                          )}
                          <div className="flex items-center justify-between pt-4 border-t border-gray-200/50">
                            {doc.metadata?.readTime && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <FaClock />
                                {doc.metadata.readTime} min read
                              </span>
                            )}
                            <span className="text-Color text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                              Read <FaArrowRight size={12} />
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div className="py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="backdrop-blur-xl bg-white/60 rounded-3xl p-12 border border-white/40 shadow-glass-hover overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-r from-Color/10 via-brightColor/10 to-accentPurple/10"></div>
              <div className="relative z-10 text-center">
                <h2 className="text-4xl font-black mb-4 bg-gradient-to-r from-Color via-brightColor to-accentPurple bg-clip-text text-transparent">
                  Ready to Get Started?
                </h2>
                <p className="text-gray-700 mb-8 max-w-2xl mx-auto text-lg">
                  Sign up for NostraHealth and start building AI-powered healthcare solutions today
                </p>
                <a
                  href={`${MAIN_SITE}/pricing`}
                  className="inline-block bg-gradient-to-r from-Color via-brightColor to-accentPurple text-white px-10 py-4 rounded-full font-bold shadow-glass hover:shadow-glass-hover hover:scale-110 transition-all duration-300"
                >
                  Start Free Trial
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="backdrop-blur-xl bg-darkPurple/90 text-white py-8 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <p className="text-gray-300">
              © {new Date().getFullYear()} NostraHealth. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default DocsHome;
